import { RefObject, useEffect, useRef, useState } from "react";

import { useIsDesktop } from "@hooks/layout";

import { compressPeaks } from "@utils/waveformDisplay";
import WaveSurfer, { WaveSurferOptions } from "wavesurfer.js";
import Hover from "wavesurfer.js/dist/plugins/hover";

const options: Partial<WaveSurferOptions> = {
  waveColor: "#c4a98e",
  progressColor: "#f8d6a7",
  cursorColor: "#f8d6a7",
  // normalize:false preserves loudness differences across tracks; the RMS
  // peaks are soft-compressed (`compressPeaks`) instead of multiplied by a
  // fixed barHeight, so loud sections keep visible dynamics rather than
  // clipping flat — see utils/waveformDisplay.ts.
  barHeight: 1,
  normalize: false,
  height: 32,
  fillParent: true,
  dragToSeek: true,
};

export type TUseWavesurfer = {
  containerRef: RefObject<HTMLDivElement>;
  peaks?: number[];
  url?: string;
  media?: HTMLMediaElement;
  isTrackPage?: boolean;
  /** Signal that the track-page container has mounted. Used as a dep so the
      effect re-runs when TrackComponent's main content (with the ref) appears. */
  trackContainerReady?: boolean;
  /**
   * Called when the user clicks/drags the waveform to seek. WaveSurfer already
   * moves the play position natively; this lets the consumer also START PLAYING
   * from that spot (gated by the caller). Kept in the consumer so the free/guest
   * play gate is honoured — the hook itself doesn't know about it.
   */
  onInteraction?: (ws: WaveSurfer) => void;
};

export const useWavesurfer = ({ url, peaks, containerRef, media, isTrackPage, trackContainerReady, onInteraction }: TUseWavesurfer) => {
  const isDesktop = useIsDesktop();
  const [wavesurfer, setWavesurfer] = useState<WaveSurfer | null>(null);
  // Held in a ref so a fresh closure each render doesn't re-run the create effect.
  const onInteractionRef = useRef(onInteraction);
  onInteractionRef.current = onInteraction;

  const updateTrackWaveform = isTrackPage ? window.location.pathname.split("/").pop() : undefined;

  useEffect(() => {
    if (!containerRef?.current) return;
    const ws = WaveSurfer.create({
      ...options,
      url,
      media,
      container: containerRef.current,
      peaks: peaks ? [compressPeaks(peaks)] : [[]],
      plugins: [
        Hover.create({
          lineColor: "rgba(99, 68, 45, 1)",
          lineWidth: 1,
          labelBackground: "rgba(99, 68, 45, 0.7)",
          labelColor: "#E0D2C1",
          labelSize: "14px",
        }),
      ],
    });

    const subscription = ws.on("finish", () => {
      ws.setTime(0);
    });

    // Click / drag-to-seek → let the consumer start playback from that position.
    // WaveSurfer has already moved the play head by the time this fires.
    const interactionSub = ws.on("interaction", () => {
      onInteractionRef.current?.(ws);
    });

    setWavesurfer(ws);

    return () => {
      const isPaused = media?.paused;
      const audioElement = document.createElement("audio");

      ws.setMediaElement(audioElement);
      ws.destroy();
      if (isTrackPage && !isPaused) {
        media?.play().catch((e) => console.log(e, "useWavesurfer error"));
      }
      subscription();
      interactionSub();
    };
  }, [url, peaks, media, isDesktop, updateTrackWaveform, trackContainerReady]);

  return wavesurfer;
};
